import styled from 'styled-components';

import { pdfToBrowser } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/placement.utils.ts';
import type { PreviewFieldBoxRes } from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';

interface Props {
	/** The boxes on THIS page, in PDF points. */
	boxes: PreviewFieldBoxRes[];
	/** The page's own height in points, for the y-flip. */
	pageHeightPt: number;
	/** CSS pixels per PDF point, as this page was rendered. */
	scale: number;
}

/**
 * Which text on the page came from a field, shown by tinting it.
 *
 * The editor's own bound-field blue, so the two views of one document agree about what a field
 * looks like. Deliberately NOT the yellow of Word's highlighter: on the editing surface that
 * colour already means "a placeholder nobody has replaced yet".
 *
 * Drawn here rather than in the render because the render is generation — the same method
 * produces a recipient's deed — and a tint that only exists in the browser cannot be executed.
 */
export const PreviewFieldOverlay = ({ boxes, pageHeightPt, scale }: Props) => (
	<Overlay data-testid="docx-preview-fields">
		{boxes.map((box, index) => {
			const browserBox = pdfToBrowser(box, pageHeightPt, scale);
			return (
				<Highlight
					// Position, not name: one field can land many times on a page, and a value
					// that wrapped is two rects of the same name on the same page.
					key={`${box.name}-${index}`}
					$left={browserBox.left}
					$top={browserBox.top}
					$width={browserBox.width}
					$height={browserBox.height}
					data-testid={`docx-preview-field-${box.name}`}
				/>
			);
		})}
	</Overlay>
);

const Overlay = styled.div`
	position: absolute;
	inset: 0;
	// Read-only: every click belongs to the page underneath.
	pointer-events: none;
`;

/**
 * The chip's hover shade rather than its resting fill, multiplied over the glyphs so the text
 * stays black. The resting fill is nearly invisible against a white page at document scale,
 * which defeats the point of marking the values at all.
 */
const Highlight = styled.div<{ $left: number; $top: number; $width: number; $height: number }>`
	position: absolute;
	left: ${({ $left }) => $left}px;
	top: ${({ $top }) => $top}px;
	width: ${({ $width }) => $width}px;
	height: ${({ $height }) => $height}px;
	background: #cfe0f5;
	mix-blend-mode: multiply;
	border-radius: 2px;
`;
